import { round } from 'lodash';

export function formatDaysHoursMinutes(ms: number, omitSeconds?: boolean) {
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  const daysMS = ms % (24 * 60 * 60 * 1000);
  const hours = Math.floor(daysMS / (60 * 60 * 1000));
  const hoursMS = ms % (60 * 60 * 1000);
  const minutes = Math.floor(hoursMS / (60 * 1000));
  const minutesMS = ms % (60 * 1000);
  const sec = Math.floor(minutesMS / 1000);

  if (!days && !hours && !minutes) {
    if (omitSeconds) {
      return '0 min';
    }
    if (!sec) {
      return '0 sec';
    }
  }

  return `${days ? `${days} days` : ''} ${hours ? `${hours} hrs` : ''} ${minutes ? `${minutes} min` : ''} ${
    sec && !omitSeconds ? `${sec} sec` : ''
  }`;
}

export function formatMinutesToHoursMinutes(minutes: number) {
  if (!minutes) {
    return '00:00';
  }

  if (minutes < 60) {
    const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;

    return `00:${formattedMinutes}`;
  }

  const remainingMinutes = minutes % 60;
  const hours = (minutes - remainingMinutes) / 60;

  // Add 0 padding if needed
  const formattedRemainingMinutes = remainingMinutes < 10 ? `0${remainingMinutes}` : `${remainingMinutes}`;
  const formattedHours = hours < 10 ? `0${hours}` : `${hours}`;

  return `${formattedHours}:${formattedRemainingMinutes}`;
}

export const convertUnit = (value: number, type: UnitType, isMetric: boolean, sigDigits = 3): string => {
  const convertedValue = convertUnitNumber(value, type, isMetric, sigDigits);

  return convertedValue.toFixed(sigDigits);
};

export type UnitType = 'pitch' | 'pressure' | 'lanceDepth' | 'lanceSpeed';

export const convertUnitNumber = (value: number, type: UnitType, isMetric: boolean, sigDigits = 3): number => {
  switch (type) {
    // Pitch is reported in inches.
    // Metric output: cm
    // Imperial output: inches
    // Conversion: 1in = 2.54 cm
    case 'pitch':
      if (isMetric) {
        return value * 2.54;
      } else {
        return value;
      }
    // Pressure is reported in PSI.
    // Metric output: BAR
    // Imperial output: PSI
    // 1PSI = 0.068947573 Bar
    case 'pressure':
      if (isMetric) {
        return value * 0.068947573;
      } else {
        return value;
      }
    // Lance Depth is reported in inches.
    // Metric output: m
    // Imperial output: ft
    case 'lanceDepth':
      if (isMetric) {
        return value * 0.0254;
      } else {
        return value / 12;
      }

    // Lance Speed is reported in inches per second.
    // Metric output: m/s
    // Imperial output: ft/s
    case 'lanceSpeed':
      if (isMetric) {
        return value * 0.0254; // 1 meter per second is .0254 meters per second
      } else {
        return value / 12;
      }

    default:
      throw Error('Unsupported unit given for conversion');
  }
};

export const unitTypeString = (type: UnitType, isMetric: boolean): string => {
  switch (type) {
    case 'pitch':
      return isMetric ? 'cm' : 'in';
    case 'pressure':
      return isMetric ? 'bar' : 'psi';
    case 'lanceDepth':
      return isMetric ? 'm' : 'ft';
    case 'lanceSpeed':
      return isMetric ? 'm/s' : 'ft/s';
    default:
      throw Error('Unsupported unit');
  }
};

export const roundValueToDecimalPlace = (value: number, decimalPlace: number): number => {
  return round(value, decimalPlace);
};
